// https://github.com/reach/observe-rect/blob/develop/src/index.ts
// Copyright (c) React Training, MIT License

const RECT_SIZE_PROPS = ["height", "width"] satisfies (keyof DOMRect)[];

const RECT_POSITION_PROPS = [
  "bottom",
  "left",
  "right",
  "top",
] satisfies (keyof DOMRect)[];

const RECT_PROPS = [
  ...RECT_SIZE_PROPS,
  ...RECT_POSITION_PROPS,
] satisfies (keyof DOMRect)[];

const observedNodes = new Map<Element, RectProps>();
let rafId: number;

export function observeRect(
  node: Element,
  callback: (rect: DOMRect) => void,
  options?: ObserveRectOptions,
) {
  const { type: observeType = "all" } = options ?? {};
  return {
    current() {
      return node.getBoundingClientRect();
    },
    observe() {
      const wasEmpty = observedNodes.size === 0;
      if (observedNodes.has(node)) {
        observedNodes.get(node)!.callbacks.push(callback);
      } else {
        observedNodes.set(node, {
          rect: undefined,
          hasRectChanged: false,
          callbacks: [callback],
        });
      }
      if (wasEmpty) {
        run(observeType);
      }
    },
    unobserve() {
      const state = observedNodes.get(node);
      if (state) {
        // Remove the callback
        const index = state.callbacks.indexOf(callback);
        if (index >= 0) {
          state.callbacks.splice(index, 1);
        }

        // Remove the node reference
        if (!state.callbacks.length) {
          observedNodes.delete(node);
        }

        // Stop the loop
        if (!observedNodes.size) {
          cancelAnimationFrame(rafId);
        }
      }
    },
  };
}

function run(type: ObserveRectOptions["type"]) {
  const changedStates: RectProps[] = [];
  observedNodes.forEach((state, node) => {
    const newRect = node.getBoundingClientRect();
    const compareFn = (() => {
      if (type === "size") {
        return rectSizeChanged;
      }
      if (type === "position") {
        return rectPositionChanged;
      }
      return rectChanged;
    })();
    if (compareFn(newRect, state.rect)) {
      state.rect = newRect;
      changedStates.push(state);
    }
  });

  changedStates.forEach((state) => {
    state.callbacks.forEach((cb) => cb(state.rect));
  });

  rafId = window.requestAnimationFrame(() => run(type));
}

export function rectChanged(
  first: Partial<DOMRect>,
  second: Partial<DOMRect> = {},
) {
  return RECT_PROPS.some((prop) => first[prop] !== second[prop]);
}

function rectSizeChanged(
  first: Partial<DOMRect>,
  second: Partial<DOMRect> = {},
) {
  return RECT_SIZE_PROPS.some((prop) => first[prop] !== second[prop]);
}

function rectPositionChanged(
  first: Partial<DOMRect>,
  second: Partial<DOMRect> = {},
) {
  return RECT_POSITION_PROPS.some((prop) => first[prop] !== second[prop]);
}

interface RectProps {
  rect: DOMRect | undefined;
  hasRectChanged: boolean;
  callbacks: Function[];
}

export type ObserveRectOptions = {
  type?: "size" | "position" | "all";
};
